import {
  Clock,
  Phone,
  PhoneDisconnect,
  PhoneIncoming,
  PhoneList,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'src/components/Button';
import { CallDetailsModal } from 'src/components/CallDetailsModal';
import { Loading } from 'src/components/Loading';
import { Tooltip } from 'src/components/Tooltip';
import { WebsocketClient } from 'src/components/WebsocketClient/client';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useDraggable } from 'src/hooks/useDraggable';
import { Can, Subject } from 'src/hooks/usePermissions';
import { Action } from 'src/interfaces/action.interface';
import {
  Agent,
  getReferencedContextParams,
} from 'src/interfaces/agent.interface';
import { Call } from 'src/interfaces/call.interface';
import { Prompt } from 'src/interfaces/prompt.interface';
import { handleCallCreationError } from 'src/utils/callErrorHandler';
import { cn } from 'src/utils/className';
import { friendlyDateFormatter } from 'src/utils/date';
import { limitString } from 'src/utils/limitString';
import { useAgentActions } from '../contexts/AgentActionsContext';
import { AnimatedCircles } from './AnimatedCircles';
import { CallContextForm } from './CallContextForm';
import { TestAgentModal } from './TestAgentModal';

const CONSTANTS = {
  MODAL_WIDTH: 308,
  MODAL_HEIGHT: 400,
  AUDIO_LEVEL_MULTIPLIER: 700,
  CALL_END_DELAY: 500,
  INITIAL_POSITION: {
    x: window.innerWidth - 308,
    y: window.innerHeight * 0.1,
  },
} as const;

interface AgentCallerProps {
  agent: Agent;
  onCallEnd?: () => void;
  onCallStart?: () => void;
  customControls?: React.ReactNode;
  isEmbedded?: boolean;
  setModalAgentNumbersOpen?: (open: boolean) => void;
  calls?: Call[];
  contextObject?: Record<string, string>;
  setContextObject?: (context: Record<string, string>) => void;
}

export const AgentCaller = ({
  agent,
  onCallEnd,
  onCallStart,
  customControls,
  isEmbedded = false,
  setModalAgentNumbersOpen,
  calls = [],
  contextObject: externalContextObject,
  setContextObject: externalSetContextObject,
}: AgentCallerProps) => {
  const { post } = useAuthedRequest();
  const notification = useNotification();

  // Add this line to get access to the actions context
  const { selectedActions, getActionsToSave } = useAgentActions();

  const startCallRef = useRef<HTMLAudioElement>(null);
  const endCallRef = useRef<HTMLAudioElement>(null);

  const websocketUrl = process.env.REACT_APP_CALL_SERVER_WEBSOCKET_URL;

  const [websocketClient, setWebsocketClient] =
    useState<WebsocketClient | null>(null);

  const [loading, setLoading] = useState(false);

  const [isCalling, setIsCalling] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isMaximized, setIsMaximized] = useState(!isEmbedded);

  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [isCallDetailsModalOpen, setIsCallDetailsModalOpen] = useState(false);
  const [internalContextObject, setInternalContextObject] = useState<
    Record<string, string>
  >({});

  // Use external if provided, otherwise use internal
  const contextObject = externalContextObject ?? internalContextObject;
  const setContextObject = externalSetContextObject ?? setInternalContextObject;

  const [callDisabled, setCallDisabled] = useState(false);
  const [agentToCall, setAgentToCall] = useState<
    (Agent & { changed: boolean }) | null
  >({ ...agent, changed: false });
  const [agentWithoutModifications] = useState<Agent | null>(agent);

  const draggable = useDraggable({
    initialPosition: CONSTANTS.INITIAL_POSITION,
    bounds: {
      maxX: window.innerWidth - CONSTANTS.MODAL_WIDTH,
      maxY: window.innerHeight - CONSTANTS.MODAL_HEIGHT,
    },
    enabled: !isMaximized,
  });

  const openModalOutboundCall = () => {
    setIsTestModalOpen(true);
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const startCall = async () => {
    try {
      setLoading(true);

      const call = await post(`/calls`, {
        agent: agentToCall?.changed ? agentToCall : (agentToCall?.id as string),
        from_number: 'web',
        to_number: 'web',
        context: contextObject,
      });

      if (!call) {
        notification.error('No response received from call creation');
        return;
      }

      if (!call.telephony_id) {
        notification.error('Call created but no telephony ID received');
        return;
      }

      startCallRef.current?.play();

      const newWs = new WebSocket(String(websocketUrl));
      newWs.onclose = async () => {
        await endCall();
      };
      newWs.onerror = async () => {
        await endCall();
      };

      setWebsocketClient(
        new WebsocketClient({
          socket: newWs,
          callSid: call.telephony_id,
          setAudioLevel: (level: number) => {
            setAudioLevel(Math.ceil(level * 700));
          },
        }),
      );

      setIsCalling(true);
      onCallStart?.();
    } catch (error) {
      handleCallCreationError(error, notification);
    } finally {
      setLoading(false);
    }
  };

  const endCall = async () => {
    if (websocketClient) {
      websocketClient.close();
      setWebsocketClient(null);
    }

    endCallRef.current?.play();

    setTimeout(() => {
      setIsCalling(false);
      onCallEnd?.();
    }, 500);
  };

  const handleCallDetails = (call: Call) => {
    setSelectedCall(call);
    setIsCallDetailsModalOpen(true);
  };

  useEffect(() => {
    return () => {
      if (websocketClient) {
        websocketClient.close();
        setWebsocketClient(null);
      }
      if (isCalling) {
        endCallRef.current?.play();
        setIsCalling(false);
      }
    };
  }, [websocketClient, isCalling]);

  useEffect(() => {
    const referencedContextParams = getReferencedContextParams(agent);
    const updatedContextObj = referencedContextParams.reduce(
      (acc, key) => {
        acc[key] = contextObject[key]?.length > 0 ? contextObject[key] : '';
        return acc;
      },
      {} as Record<string, string>,
    );

    setContextObject(updatedContextObj);
  }, [agent.prompt, agent.initial_message, agent.voicemail_message]);

  useEffect(() => {
    const compareAndSetAgentToCall = async () => {
      try {
        const currentAgentString = JSON.stringify(agent);
        const originalAgentString = JSON.stringify(agentWithoutModifications);

        // Check if actions have changed by comparing with agent.actions
        const currentAgentActionIds = Array.isArray(agent.actions)
          ? agent.actions.map((a: string | Action) =>
              typeof a === 'string' ? a : a.id,
            )
          : [];
        const actionsChanged =
          JSON.stringify(currentAgentActionIds.sort()) !==
          JSON.stringify(selectedActions.sort());

        if (currentAgentString !== originalAgentString || actionsChanged) {
          // If there is a diff, send the modified agent without id
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, ...newAgentToCall } = agent;

          // Use the context function to get actions to save
          const actionsToSave = getActionsToSave();

          setAgentToCall({
            ...newAgentToCall,
            actions: actionsToSave as
              | string[]
              | (Action & { originalId?: string })[],
            changed: true,
          });
        } else {
          // If no diff, send the current agent with id
          setAgentToCall({ ...agent, changed: false });
        }
      } catch (error) {
        console.warn('Failed to serialize agent for comparison:', error);
      }
    };

    if (agent) {
      compareAndSetAgentToCall();
    }
  }, [agent, selectedActions]); // Add dependencies

  useEffect(() => {
    setCallDisabled(
      !agent.voice ||
        !agent.prompt ||
        String(agent.prompt) === '' ||
        (agent.prompt as Prompt).content === '',
    );
  }, [agent]);

  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center p-4 md:p-8 lg:p-16 w-full relative',
        !isEmbedded && 'h-full',
      )}
      style={{
        backgroundImage: !isEmbedded
          ? `url(/assets/agents/bg-call.png)`
          : 'none',
        backgroundRepeat: 'repeat',
        background: !isEmbedded
          ? `linear-gradient(345.95deg, rgba(221, 214, 255, 0.13) 14.69%, rgba(255, 190, 244, 0.13) 31.81%, rgba(255, 209, 203, 0.13) 54.58%, rgba(255, 250, 231, 0.13) 75.9%, rgba(213, 255, 237, 0.13) 93.27%), url(/assets/agents/bg-call.png)`
          : 'none',
        backgroundBlendMode: 'overlay',
      }}
    >
      <audio
        ref={startCallRef}
        preload="auto"
        src="/assets/agents/start-call.mp3"
        className="hidden"
      />

      <audio
        ref={endCallRef}
        preload="auto"
        src="/assets/agents/end-call.mp3"
        className="hidden"
      />

      {!isEmbedded && isCalling && (
        <div
          className={clsx(
            'fixed inset-0 w-full h-full z-50',
            isMaximized ? 'bg-neutral-700/50' : 'pointer-events-none',
          )}
        >
          <div
            className={clsx(
              'bg-neutral-700',
              'fixed',
              'flex',
              'flex-col',
              isMaximized
                ? [
                    'min-h-[90vh]',
                    'min-w-[80vw]',
                    'top-1/2',
                    'left-1/2',
                    '-translate-x-1/2',
                    '-translate-y-1/2',
                    'rounded-3xl',
                  ]
                : [
                    'h-[400px]',
                    'w-[300px]',
                    'pointer-events-auto',
                    draggable.isDragging ? 'cursor-grabbing' : 'cursor-grab',
                    'rounded-xl',
                  ],
            )}
            style={
              !isMaximized
                ? {
                    top: `${draggable.position.y}px`,
                    left: `${draggable.position.x}px`,
                    transition: draggable.isDragging
                      ? 'none'
                      : 'all 0.2s ease-in-out',
                  }
                : undefined
            }
            {...(!isMaximized ? draggable.handlers : {})}
          >
            <div className="flex justify-end p-4 gap-4">
              {!isEmbedded && (
                <Tooltip content="Maximize" theme="light" position="bottom">
                  <button
                    type="button"
                    className="bg-transparent border-0 active:opacity-70"
                    onClick={toggleMaximize}
                  >
                    <img
                      src="/assets/agents/maximize.svg"
                      className="w-6 h-6 select-none"
                      alt="Maximize"
                      style={{
                        transform: isMaximized
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                    />
                  </button>
                </Tooltip>
              )}

              <Tooltip content="End Call" theme="light" position="bottom">
                <button
                  type="button"
                  className="bg-transparent border-0 active:opacity-70"
                  onClick={endCall}
                >
                  <img
                    src="/assets/agents/cancel.svg"
                    className="w-6 h-6 select-none"
                    alt="Cancel"
                  />
                </button>
              </Tooltip>
            </div>

            <div className="container mx-auto flex-1">
              <div
                className={clsx(
                  'flex flex-col items-center justify-center',
                  isMaximized ? 'p-8 min-h-[80vh]' : 'p-4',
                )}
              >
                <AnimatedCircles
                  isMaximized={isMaximized}
                  isCalling={isCalling}
                  audioLevel={audioLevel}
                  variant={isMaximized ? 'large' : 'small'}
                />

                <p
                  className={clsx(
                    'text-neutral-0 mt-6',
                    isMaximized ? 'text-2xl mb-12' : 'text-xl mb-6 text-center',
                  )}
                >
                  {agent?.name || 'Agent'} is{' '}
                  {isCalling
                    ? audioLevel > 1
                      ? 'speaking...'
                      : 'listening...'
                    : 'waiting for your call...'}
                </p>
              </div>
            </div>

            <div
              className={clsx(
                'bg-neutral-700',
                isMaximized ? 'p-4 rounded-b-3xl' : 'p-3 rounded-b-xl',
              )}
            >
              <div className="flex justify-center">
                {!isCalling && (
                  <button
                    className={clsx(
                      'rounded-lg bg-primary text-neutral-0 flex items-center justify-center gap-2',
                      isMaximized ? 'px-6 py-3' : 'px-4 py-2 text-sm w-full',
                    )}
                    onClick={startCall}
                    disabled={callDisabled}
                  >
                    <PhoneDisconnect size={isMaximized ? 20 : 16} />
                    Test Call Agent
                  </button>
                )}
                {isCalling && (
                  <button
                    className={clsx(
                      'rounded-lg bg-error text-neutral-0 flex items-center justify-center gap-2',
                      isMaximized ? 'px-6 py-3' : 'px-4 py-2 text-sm w-full',
                    )}
                    onClick={endCall}
                  >
                    <PhoneDisconnect size={isMaximized ? 20 : 16} />
                    End Call
                  </button>
                )}
              </div>
            </div>

            {customControls}
          </div>
        </div>
      )}

      {isEmbedded && (
        <div className="flex flex-col items-center justify-center w-full min-h-[60vh]">
          <div className="container mx-auto flex-1 mt-12">
            <div
              className={clsx(
                'flex flex-col items-center justify-center p-8 m-2',
              )}
            >
              <AnimatedCircles
                isMaximized
                isCalling={isCalling}
                audioLevel={audioLevel}
                variant="large"
              />

              <p className="text-2xl text-neutral-0 mt-6 mb-6">
                {!isCalling
                  ? 'Talk to an agent live in your browser'
                  : audioLevel > 1
                    ? 'Agent is speaking...'
                    : 'Agent is listening...'}
              </p>

              <div className="flex flex-col gap-4 w-full max-w-md">
                {!isCalling && (
                  <button
                    className="px-6 py-3 rounded-lg bg-primary text-neutral-0 flex items-center justify-center gap-2 w-full"
                    onClick={startCall}
                    disabled={callDisabled}
                  >
                    <Phone size={20} />
                    Start Browser Call
                  </button>
                )}

                {isCalling && (
                  <button
                    className="px-6 py-3 rounded-lg bg-error text-neutral-0 flex items-center justify-center gap-2 w-full"
                    onClick={endCall}
                  >
                    <PhoneDisconnect size={20} />
                    End Call
                  </button>
                )}

                {customControls}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isEmbedded && !isCalling && (
        <>
          <div
            className="flex w-full p-4 flex-col items-center gap-6 rounded-2xl bg-neutral-0 max-w-[300px]"
            style={{
              boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div className="relative w-full flex flex-col items-center justify-center py-2">
              <AnimatedCircles
                isMaximized={false}
                isCalling={isCalling}
                audioLevel={audioLevel}
                variant="small"
              />
              <div className="flex flex-col items-center gap-2 mt-6 w-full">
                <span className="text-neutral-700 text-center">
                  {agent.name ? limitString(agent.name, 25) : 'Agent'}
                </span>

                <Button
                  onClick={isCalling ? endCall : startCall}
                  className={cn(
                    'flex items-center gap-2 w-full',
                    loading && 'opacity-70',
                  )}
                  disabled={callDisabled || loading}
                >
                  {loading && (
                    <>
                      <Loading size="4" className="mr-2" />
                      Creating call...
                    </>
                  )}

                  {!loading && (
                    <>
                      {isCalling ? (
                        <PhoneDisconnect size={20} />
                      ) : (
                        <Phone size={20} />
                      )}
                      {isCalling ? 'End Call' : 'Test Call Agent'}
                    </>
                  )}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => openModalOutboundCall()}
                  className="flex items-center gap-2 w-full"
                  disabled={callDisabled}
                >
                  <PhoneIncoming size={20} />
                  Send a Call
                </Button>

                {agent.id && setModalAgentNumbersOpen && (
                  <Can operation="update" subject={Subject.AGENTS}>
                    <Button
                      variant="outlined"
                      onClick={() => setModalAgentNumbersOpen(true)}
                      className="flex items-center gap-2 w-full"
                    >
                      <PhoneList size={20} />
                      Setup Inbound Calls
                    </Button>
                  </Can>
                )}

                <TestAgentModal
                  isOpen={isTestModalOpen}
                  onClose={() => {
                    setIsTestModalOpen(false);
                  }}
                  agent={agentToCall}
                  callContext={contextObject}
                  disableCallContext={true}
                />

                {calls.length > 0 && (
                  <>
                    <div className="w-full h-[2px] bg-neutral-100" />
                    <Tooltip
                      content={`Caller: ${calls[0].from_number} | Time: ${friendlyDateFormatter(
                        calls[0].start_time
                          ? new Date(
                              parseInt(calls[0].start_time.toString()) * 1000,
                            )
                          : null,
                      )}`}
                      position="bottom"
                      className="w-full"
                    >
                      <Button
                        variant="outlined"
                        onClick={() => handleCallDetails(calls[0])}
                        className="flex items-center gap-2 w-full"
                      >
                        <Clock size={20} />
                        Most Recent Call
                      </Button>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          </div>

          {Object.keys(contextObject).length > 0 && (
            <div
              className="flex w-full p-4 flex-col items-center rounded-2xl bg-neutral-0 max-w-[300px] mt-4"
              style={{
                boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
              }}
            >
              <span className="text-xl font-bold text-primary text-center">
                Call Context
              </span>

              <CallContextForm
                contextObject={contextObject}
                setContextObject={setContextObject}
              />
            </div>
          )}

          {selectedCall && (
            <CallDetailsModal
              call={selectedCall}
              isOpen={isCallDetailsModalOpen}
              onClose={() => setIsCallDetailsModalOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
};
